import type { components } from '@connected/api-client/api.generated';
import type { RadioGroupOption } from '@connected/form-fields-v2/components/form/radio-group';
import type { ColumnDef } from '@tanstack/react-table';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Checkbox } from '@connected/ui/components/checkbox';
import { Item } from '@connected/ui/components/item';
import { Popout, PopoutBody, PopoutContent, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ChevronDown, ChevronRight, Circle, Edit, PlusIcon, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DataTable } from '@/components/data-table';
import { ExpandedRowsMobile } from '@/components/expanded-row';
import { LoadingPage } from '@/components/loading-page';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export const Route = createFileRoute('/_auth/dashboard/organizations/$organizationId/settings/norms/')({
    component: RouteComponent,
});

type ListNormItem = components['schemas']['ListElectricalInspectionNormItemQueryResultItem'];

function NormModal({
    isOpen,
    setIsOpen,
    norm,
    inspectionNormId,
    count,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    norm: components['schemas']['ListElectricalInspectionNormItemQueryResultItem'] | null;
    inspectionNormId: string;
    count: number;
}) {
    const { t } = useTranslation('dashboard');

    const [loading, setIsLoading] = useState<boolean>(false);

    const DEFAULT_VALUES: ListNormItem = {
        Name: norm?.Name ?? null,
        Description: norm?.Description ?? null,
        InspectionKinds: norm?.InspectionKinds ?? [],
        NormKind: norm?.NormKind ?? null!,
        IsCustomInput: norm?.IsCustomInput ?? false,
    };

    const institutesList: RadioGroupOption<ListNormItem['NormKind']>[] = [
        {
            value: 'NEN',
            label: 'NEN',
        },
        {
            value: 'SCIOS',
            label: 'SCIOS',
        },
        {
            value: 'NPR',
            label: 'NPR',
        },
        {
            value: 'NTA',
            label: 'NTA',
        },
        {
            value: 'Other',
            label: 'Other',
        },

    ];
    const inspectionKindsList = [
        {
            value: 'Scope8',
            label: 'Scope 8',
        },
        {
            value: 'Scope10',
            label: 'Scope 10',
        },
        {
            value: 'Scope12',
            label: 'Scope 12',
        },
        {
            value: 'Nen1010',
            label: 'Nen 1010',
        },
        {
            value: 'Nen3140',
            label: 'Nen 3140',
        },
    ];

    const updateExistingNorm = api.useMutation(
        'put',
        '/api/v2/electrical/inspectionnorms/items/edit',
        {
            onSuccess: () => {
                toast.success(t('dashboard.settings.norms.messages.updateSuccess'));
                queryClient.invalidateQueries(portaalQueryOptions.norms.ListElectricalInspectionNormItems({ ElectricalInspectionNormListId: inspectionNormId })).then(() => {
                    setIsOpen(false);
                });
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'EditElectricalInspectionNormItemCommand',
                        error,
                    },
                    { apiHttpMethod: 'Put', apiUrl: '/api/v2/electrical/inspectionnorms/items/edit' },
                );
                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
            onMutate: () => setIsLoading(true),
        },
    );

    const createNewNorm = api.useMutation(
        'post',
        '/api/v2/electrical/inspectionnorms/items/create',
        {
            onSuccess: () => {
                toast.success(t('dashboard.settings.norms.messages.createSuccess'));

                queryClient.invalidateQueries(portaalQueryOptions.norms.ListElectricalInspectionNormItems({ ElectricalInspectionNormListId: inspectionNormId })).then(() => {
                    setIsOpen(false);
                });
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'CreateElectricalInspectionNormItemCommand',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/inspectionnorms/items/edit' },
                );
                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
            onMutate: () => setIsLoading(true),
        },
    );

    const form = useAppForm(
        {
            defaultValues: DEFAULT_VALUES,
            onSubmit: (form) => {
                if (norm) {
                    updateExistingNorm.mutateAsync({
                        body: {
                            ElectricalInspectionNormItemId: norm.ElectricalInspectionNormItemId!,
                            NormKind: (form?.value?.NormKind ?? 'Other') as 'Other' | 'NEN' | 'SCIOS' | 'NPR' | 'NTA',
                            InspectionKinds: form?.value?.InspectionKinds,
                            Name: form?.value?.Name,
                            Description: form?.value?.Description,
                            Order: norm.Order ?? count + 1,
                            IsCustomInput: norm?.IsCustomInput ?? false,
                        },
                    });
                }
                else {
                    createNewNorm.mutateAsync({
                        body: {
                            ElectricalInspectionNormListId: inspectionNormId,
                            NormKind: (form?.value?.NormKind ?? 'Other') as 'Other' | 'NEN' | 'SCIOS' | 'NPR' | 'NTA',
                            InspectionKinds: form?.value?.InspectionKinds,
                            Name: form?.value?.Name,
                            Order: count + 1,
                            Description: form?.value?.Description,
                            IsCustomInput: DEFAULT_VALUES?.IsCustomInput ?? false,
                        },
                    });
                }
            },
        },
    );
    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent className="max-w-[640px]">
                <PopoutHeader>
                    <PopoutTitle>
                        {!norm
                            ? (
                                    t('dashboard.settings.norms.create.title')
                                )
                            : (
                                    t('dashboard.settings.norms.edit.title')
                                )}

                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form onSubmit={handleSubmit} className="flex ga-2 flex-col ">
                        <form.AppField
                            name="Name"
                            validators={{
                                onChange: validators.editElectricalInspectionNormItemCommand.name,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.organizations.table.headers.name')} />
                                    <field.Input
                                        placeholder={t('dashboard.settings.measuring-instruments.create.namePlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >

                        </form.AppField>
                        <form.AppField
                            name="NormKind"
                            validators={{
                                onChange: validators.editElectricalInspectionNormItemCommand.normKind,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.norms.tables.institutes')} />
                                    <field.RadioGroup
                                        className="flex flex-row gap-0"
                                        options={institutesList}
                                        // TODO: refactor to form fields component
                                        renderItem={item => (
                                            <Item
                                                variant="outline"
                                                onClick={() => item.onSelect?.(item.option.value as 'Other' | 'NEN' | 'SCIOS' | 'NPR' | 'NTA')}
                                                className="flex-1 border-t text-sm flex-row border-b hover:cursor-pointer border-r-transparent rounded-none p-2 m-0 justify-center first:rounded-s-md last:rounded-e-md last:border-r-border"
                                            >
                                                <Item.Content>
                                                    <Item.Title>{item.option.label}</Item.Title>
                                                    {item.option?.description && <Item.Description>{item.option.description}</Item.Description>}

                                                </Item.Content>
                                                <Item.Content>
                                                    <Checkbox
                                                        checked={item?.selected}

                                                        className="rounded-3xl p-2 !!bg-brand-solid"
                                                        icon={<Circle fill="white" className="absolute" size={6} />}
                                                    />
                                                </Item.Content>
                                            </Item>
                                        )}
                                    >
                                    </field.RadioGroup>
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >

                        </form.AppField>
                        <form.AppField
                            name="InspectionKinds"
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.norms.tables.inspectionKinds')} />
                                    <field.Popover>
                                        <field.CheckboxGroup options={inspectionKindsList}>
                                            <field.Popover.Trigger>
                                                <field.CheckboxGroup.Trigger placeholder="Select options" />
                                            </field.Popover.Trigger>
                                            <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6">
                                                <field.CheckboxGroup options={inspectionKindsList} />
                                            </field.Popover.Content>
                                        </field.CheckboxGroup>
                                    </field.Popover>
                                </field.Label>
                            )}
                        >

                        </form.AppField>
                        <form.AppField
                            name="Description"
                            validators={{
                                onChange: validators.editElectricalInspectionNormItemCommand.description,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.organizations.table.headers.description')} />
                                    <field.Textarea placeholder={t('dashboard.settings.norms.tables.descriptionPlaceholder')}></field.Textarea>
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >

                        </form.AppField>
                        <div className="w-full flex flex-row gap-2 mt-5">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button type="submit" variant="default" className="w-full" disabled={loading}>
                                {!norm
                                    ? (
                                            t('dashboard.create')
                                        )
                                    : (
                                            t('dashboard.save')
                                        )}
                            </Button>
                        </div>
                    </form>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
}

function RouteComponent() {
    const { organizationId } = Route.useParams();
    const [isOpenCreateNorm, setOpenCreateNorm] = useState<boolean>(false);
    const { t } = useTranslation('dashboard');
    const search = Route.useSearch() as any;
    const [selectedNorm, setSelectedNorm] = useState<components['schemas']['ListElectricalInspectionNormItemQueryResultItem'] | null>(null);
    const electricalInspection = useQuery(
        portaalQueryOptions.norms.ListElectricalInspectionActiveNormList({ organizationId }),
    );

    const { currentUser } = useAuthentication();

    const [expandedId, setExpandedId] = useState<string | null>(null);

    // its filtered in the portaal optios that it grabs active norm list so this works
    const electricalInspectionNormListId = useMemo(() => {
        return electricalInspection?.data?.Items?.[0]?.ElectricalInspectionNormListId ?? null;
    }, [electricalInspection?.data?.Items]);

    const normListOptions = useQuery({
        ...portaalQueryOptions.norms.ListElectricalInspectionNormItems({ ElectricalInspectionNormListId: electricalInspectionNormListId ?? '' }),
        enabled: !!electricalInspectionNormListId,
    });

    const deleteExitingNorm = api.useMutation(
        'delete',
        '/api/v2/electrical/inspectionnorms/items/delete',
        {
            onSuccess: () => {
                toast.success(t('dashboard.settings.norms.messages.deleteSuccess'));
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'DeleteElectricalInspectionNormItemCommand',
                        error,
                    },
                    { apiHttpMethod: 'Delete', apiUrl: '/api/v2/electrical/inspectionnorms/items/delete' },
                );
                toast.error(error?.Message ?? t('dashboard.general.messages.error'));
            },
            onSettled: () => {
                queryClient.invalidateQueries(portaalQueryOptions.norms.ListElectricalInspectionNormItems({ ElectricalInspectionNormListId: electricalInspectionNormListId ?? '' }));
            },
        },
    );

    const columns = useMemo<ColumnDef<ListNormItem>[]>(() => [
        {
            accessorKey: 'Name',
            meta: {
                sticky: false,
                width: 10,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.organizations.table.headers.name') || '-'}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="medium">
                    <Button
                        variant="ghost"
                        className="sm:hidden mr-2"
                        onClick={() => setExpandedId(prev => prev === row?.original?.ElectricalInspectionNormItemId ? null : row?.original?.ElectricalInspectionNormItemId ?? null)}
                    >
                        {expandedId === row?.original?.ElectricalInspectionNormItemId ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    {row.original?.Name}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'NormKind',
            meta: {
                sticky: false,
                width: 10,
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.measuring-instruments.create.calibrationKind') || '-'}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="medium">
                    {row.original?.NormKind}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'InspectionKinds',
            meta: {
                sticky: false,
                width: 10,
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.standardIn.title')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="medium">
                    {row.original?.InspectionKinds?.map((kind: string) => kind).join(', ') || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Description',
            meta: {
                sticky: false,
                width: 200,
                mobileHidden: true,

            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.organizations.table.headers.description')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const desc = row.original?.Description || '';
                const shortDesc = desc.length > 50 ? `${desc.slice(0, 50)}...` : desc;
                return (
                    <DataTable.Cell variant="medium">
                        <span className="hidden sm:inline">{shortDesc || '-'}</span>
                        <span className="sm:hidden">{desc || '-'}</span>
                    </DataTable.Cell>
                );
            },
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: (props: any) => {
                return (
                    <div className="flex gap-1 items-center justify-end ml-auto">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={async () => {
                                if (await showConfirm(t('dashboard.settings.norms.message.confirmDelete'))) {
                                    deleteExitingNorm.mutateAsync({
                                        params: {
                                            query: {
                                                ElectricalInspectionNormItemId: props.row.original?.ElectricalInspectionNormItemId ?? '',
                                            },
                                        },
                                    });
                                }
                            }}
                        >
                            <Trash2 className="text-text-primary/40 size-5" />
                        </Button>
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={() => {
                                setSelectedNorm(props.row.original);
                                setOpenCreateNorm(true);
                            }}
                        >
                            <Edit className="text-text-primary/40 size-5" />
                        </Button>
                    </div>
                );
            },
        },
    ], [deleteExitingNorm, expandedId, t]);

    const tableColumns = useMemo(
        () => normListOptions.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, normListOptions.isLoading],
    );

    const defaultListActivate = api.useMutation('put', '/api/v2/electrical/inspectionnorms/lists/activate');

    const defaultNorms = api.useMutation('post', '/api/v2/electrical/inspectionnorms/lists/createdefault');

    const tableData = useMemo<components['schemas']['ListElectricalInspectionNormItemQueryResultItem'][]>(
        () => {
            if (normListOptions.isError) {
                toast.error(normListOptions.error?.Message);
                return [];
            }
            const searchTerm = search.search?.toLowerCase() || '';

            return normListOptions.data?.Items?.filter((norm) => {
                if (!searchTerm)
                    return true;

                return [
                    norm.ElectricalInspectionNormItemId || '',
                    norm.Name || '',
                    norm.Description || '',
                    norm.NormKind || '',
                    norm.InspectionKinds?.map(kind => kind).join('') || '',
                ].some(field => field?.toString().toLowerCase().includes(searchTerm));
            }) || [];
        },
        [normListOptions.isError, normListOptions.data?.Items, normListOptions.error?.Message, search.search],
    );

    if (electricalInspection.isLoading || normListOptions.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div className="flex flex-col md:flex-wrap gap-8 justify-start">
            <div className="w-full">
                <DataTable.TableHead
                    title="Normen"
                    subtitle="Bewerk normen die relevant zijn voor uw inspectie"
                    buttons={[
                        {
                            name: 'Norm',
                            icon: <PlusIcon size={19} />,
                            callback: () => {
                                setSelectedNorm(null);
                                setOpenCreateNorm(true);
                            },
                        },
                        ...(currentUser?.IsAdmin
                            ? [{
                                    name: 'Default List',
                                    icon: <PlusIcon size={19} />,
                                    callback: async () => {
                                        if (await showConfirm('Weet je zeker dat je een standaardlijst wilt maken? Hierdoor wordt de huidige lijst gearchiveerd.')) {
                                            defaultNorms.mutateAsync({
                                                body: {
                                                    Description: 'Default Norms List',
                                                    OrganizationId: organizationId,
                                                },
                                            }).then((response) => {
                                                defaultListActivate.mutateAsync({
                                                    body: {
                                                        ElectricalInspectionNormListId: response?.ElectricalInspectionNormListId,
                                                    },
                                                }).then(() => {
                                                    toast('Aangemaakt. Reload.');
                                                });
                                            });
                                        }
                                    },
                                }]
                            : []
                        ),
                    ]}
                    search={Route}
                />
                <DataTable
                    className="rounded-tl-none rounded-tr-none"
                    columns={tableColumns}
                    data={tableData}
                    pageSize={10}
                    expandedRow={row =>
                        expandedId && expandedId === row?.original?.ElectricalInspectionNormItemId
                            ? (
                                    <ExpandedRowsMobile columns={columns} data={row.original} />
                                )
                            : null}
                />
            </div>
            {isOpenCreateNorm && (
                <NormModal isOpen={isOpenCreateNorm} count={normListOptions.data?.Count ?? 0} setIsOpen={setOpenCreateNorm} norm={selectedNorm} inspectionNormId={electricalInspectionNormListId ?? ''} />
            )}
        </div>
    );
}
